// Core amortization engine: SAC & Price, monetary correction and extra amortization.
import { monthLabel } from "./format";
import type { AmortizationSystem } from "./rates";

export type ExtraMode = "term" | "installment";

export interface Contribution {
  amount: number;
  startMonth: number;
  recurring: boolean;
  intervalMonths: number;
  endMonth?: number;
}

export interface Insurance {
  amount: number;
  startMonth?: number;
  endMonth?: number;
}

export interface SimulationInput {
  principal: number;
  monthlyRate: number;
  termMonths: number;
  system: AmortizationSystem;
  monthlyCorrection: number;
  startDate: Date;
  extraMode: ExtraMode;
  contributions: Contribution[];
  insurances?: Insurance[];
}

export interface ScheduleRow {
  month: number;
  label: string;
  openingBalance: number;
  correctedBalance: number;
  interest: number;
  amortization: number;
  extra: number;
  correction: number;
  payment: number;
  balance: number;
}

export interface Schedule {
  rows: ScheduleRow[];
  totalInterest: number;
  totalPaid: number;
  totalExtra: number;
  totalCorrection: number;
  termMonths: number;
  principal: number;
  firstPayment: number;
  lastPayment: number;
}

function pricePayment(balance: number, i: number, remaining: number): number {
  const rem = Math.max(remaining, 1);
  if (i <= 0) return balance / rem;
  return (balance * i) / (1 - Math.pow(1 + i, -rem));
}

function extraForMonth(month: number, contributions: Contribution[]): number {
  let total = 0;
  for (const c of contributions) {
    if (c.amount <= 0 || c.startMonth < 1) continue;
    const end = c.endMonth && c.endMonth > 0 ? c.endMonth : Infinity;
    if (month > end) continue;
    if (month === c.startMonth) {
      total += c.amount;
    } else if (c.recurring) {
      const interval = Math.max(1, Math.round(c.intervalMonths));
      if (month > c.startMonth && (month - c.startMonth) % interval === 0) {
        total += c.amount;
      }
    }
  }
  return total;
}

function insuranceForMonth(month: number, insurances: Insurance[] | undefined): number {
  if (!insurances || insurances.length === 0) return 0;
  let total = 0;
  for (const ins of insurances) {
    if (!(ins.amount > 0)) continue;
    const start = ins.startMonth && ins.startMonth > 0 ? ins.startMonth : 1;
    const end = ins.endMonth && ins.endMonth > 0 ? ins.endMonth : Infinity;
    if (month >= start && month <= end) total += ins.amount;
  }
  return total;
}

interface BuildOptions {
  withExtra: boolean;
}

function buildSchedule(input: SimulationInput, opts: BuildOptions): Schedule {
  const { principal, monthlyRate: i, termMonths: n, system, monthlyCorrection } = input;
  const c = Math.max(monthlyCorrection, 0);
  const rows: ScheduleRow[] = [];
  let balance = principal;
  let totalInterest = 0;
  let totalPaid = 0;
  let totalExtra = 0;
  let totalCorrection = 0;

  const basePricePMT = pricePayment(principal, i, n);
  const baseSacAmort = principal / n;
  const useTermMode = opts.withExtra && input.extraMode === "term";

  let month = 0;
  let correctionFactor = 1;
  const guard = n + 1200;

  while (balance > 0.005 && month < guard) {
    month++;
    const remaining = n - (month - 1);
    const openingBalance = balance;
    const correctionAmount = balance * c;
    balance += correctionAmount;
    correctionFactor *= 1 + c;
    const correctedBalance = balance;
    totalCorrection += correctionAmount;

    const interest = balance * i;
    let payment: number;
    let amort: number;

    if (system === "PRICE") {
      payment = useTermMode ? basePricePMT * correctionFactor : pricePayment(balance, i, remaining);
      amort = payment - interest;
    } else {
      amort = useTermMode ? baseSacAmort * correctionFactor : balance / Math.max(remaining, 1);
      payment = amort + interest;
    }

    if (amort < 0) amort = 0;
    if (amort > balance) {
      amort = balance;
      payment = amort + interest;
    }
    balance -= amort;

    let extra = 0;
    if (opts.withExtra) {
      extra = extraForMonth(month, input.contributions);
      if (extra > balance) extra = balance;
      balance -= extra;
    }

    const insurance = insuranceForMonth(month, input.insurances);
    payment += insurance;

    totalInterest += interest;
    totalPaid += payment + extra;
    totalExtra += extra;

    rows.push({
      month,
      label: monthLabel(input.startDate, month - 1),
      openingBalance,
      correctedBalance,
      interest,
      amortization: amort,
      extra,
      correction: correctionAmount,
      payment,
      balance: Math.max(balance, 0),
    });

    if (balance <= 0.005) break;
  }

  return {
    rows,
    totalInterest,
    totalPaid,
    totalExtra,
    totalCorrection,
    termMonths: rows.length,
    principal,
    firstPayment: rows[0]?.payment ?? 0,
    lastPayment: rows[rows.length - 1]?.payment ?? 0,
  };
}

export interface Comparison {
  interestSaved: number;
  interestSavedPercent: number;
  monthsSaved: number;
  paymentReduction: number;
  totalPaidSaved: number;
}

export interface SimulationResult {
  baseline: Schedule;
  withExtra: Schedule | null;
  comparison: Comparison | null;
  hasExtra: boolean;
}

export function simulate(input: SimulationInput): SimulationResult {
  const baseline = buildSchedule(input, { withExtra: false });

  const hasExtra = input.contributions.some((c) => c.amount > 0 && c.startMonth >= 1);
  if (!hasExtra) {
    return { baseline, withExtra: null, comparison: null, hasExtra: false };
  }

  const withExtra = buildSchedule(input, { withExtra: true });
  const interestSaved = baseline.totalInterest - withExtra.totalInterest;
  const comparison: Comparison = {
    interestSaved,
    interestSavedPercent:
      baseline.totalInterest > 0 ? (interestSaved / baseline.totalInterest) * 100 : 0,
    monthsSaved: baseline.termMonths - withExtra.termMonths,
    paymentReduction: baseline.lastPayment - withExtra.lastPayment,
    totalPaidSaved: baseline.totalPaid - withExtra.totalPaid,
  };

  return { baseline, withExtra, comparison, hasExtra: true };
}

export function scheduleToCSV(schedule: Schedule): string {
  const header = [
    "Mês", "Referência", "Dívida", "Correção", "Dívida corrigida",
    "Juros", "Amort. mensal", "Amortização extra", "Parcela", "Saldo",
  ].join(";");

  const lines = schedule.rows.map((r) =>
    [
      r.month,
      r.label,
      r.openingBalance.toFixed(2).replace(".", ","),
      r.correction.toFixed(2).replace(".", ","),
      r.correctedBalance.toFixed(2).replace(".", ","),
      r.interest.toFixed(2).replace(".", ","),
      r.amortization.toFixed(2).replace(".", ","),
      r.extra.toFixed(2).replace(".", ","),
      r.payment.toFixed(2).replace(".", ","),
      r.balance.toFixed(2).replace(".", ","),
    ].join(";"),
  );

  return [header, ...lines].join("\n");
}
